import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ResponsiveDialog from '../UI/Dialogs/ResponsiveDialog';
import { useBulkCreateBillingConfigs, useMissingBillingConfigs } from '../../hooks/api/budgetBillingConfig';
import LoadError from './LoadError';

/** One partner / location / worktype triple already present in the target bucket. */
export interface BucketSignature {
    partnerName: string;
    locationName: string;
    worktypeName: string;
}

/** The fields a proposal can be matched on, in the order the checkbox row shows them. */
const MATCH_FIELDS = [
    { field: 'partnerName', labelKey: 'field.partner' },
    { field: 'locationName', labelKey: 'field.location' },
    { field: 'worktypeName', labelKey: 'field.worktype' },
] as const;

type MatchField = (typeof MATCH_FIELDS)[number]['field'];

interface CompleteMissingConfigsDialogProps {
    open: boolean;
    onClose: () => void;
    // Always a concrete bucket — the toolbar action is disabled in the "All buckets" view, because a
    // created row has to belong to one bucket.
    bucketId: number;
    bucketName: string;
    bucketSignatures: BucketSignature[];
    /** The bucket's draft, so the created rows get their ABT and forecast seeded (CALL-780). */
    seedVersionId?: number;
}

// CALL-780: creates the Billing Configs for combinations that are active in Strategic Volume but have no
// config yet. The rows arrive from the backend already labelled and sorted.
const CompleteMissingConfigsDialog = ({
    open,
    onClose,
    bucketId,
    bucketName,
    bucketSignatures,
    seedVersionId,
}: CompleteMissingConfigsDialogProps) => {
    const { t } = useTranslation('budget');
    const { rows, isFetching, isError, error, refetch } = useMissingBillingConfigs(bucketId, open);
    const { mutateAsync: bulkCreateAsync, isLoading: isCreating } = useBulkCreateBillingConfigs(seedVersionId);

    // Which fields decide whether a proposal "belongs" to this bucket. All three by default: the whole
    // triple is the narrowest, most obviously-mine group. Unticking one widens the preselection — on dev
    // data Partner+Location+Worktype matched 8 proposals, Partner+Location 104, Partner alone 127.
    const [matchOn, setMatchOn] = useState<Record<MatchField, boolean>>({
        partnerName: true,
        locationName: true,
        worktypeName: true,
    });

    const activeFields = useMemo(() => MATCH_FIELDS.filter(({ field }) => matchOn[field]), [matchOn]);

    const bucketKeys = useMemo(() => {
        const key = (signature: BucketSignature) => activeFields.map(({ field }) => signature[field] ?? '').join('|');
        return new Set(bucketSignatures.map(key));
    }, [bucketSignatures, activeFields]);

    const matches = useCallback(
        (row: { partnerName: string | null; locationName: string | null; worktypeName: string | null }) =>
            activeFields.length > 0 && bucketKeys.has(activeFields.map(({ field }) => row[field] ?? '').join('|')),
        [activeFields, bucketKeys]
    );

    // Only the user's explicit clicks are stored; everything else falls back to `matches`. Query data
    // arrives after the first render and changes on every refetch, so a plain selected-id set would have
    // to be re-seeded by an effect — which would also silently undo the user's own choices.
    const [overrides, setOverrides] = useState<Map<number, boolean>>(new Map());

    useEffect(() => {
        if (!open) setOverrides(new Map());
    }, [open]);

    // Changing the bucket, or the match criteria, means "work out the preselection again" — so the
    // explicit choices made against the previous criteria are dropped with it.
    useEffect(() => {
        setOverrides(new Map());
    }, [bucketId, matchOn]);

    const isSelected = useCallback(
        (row: {
            workingUnitLocationId: number;
            partnerName: string | null;
            locationName: string | null;
            worktypeName: string | null;
        }) => overrides.get(row.workingUnitLocationId) ?? matches(row),
        [overrides, matches]
    );

    // Matching proposals first. Array#sort is stable, so the backend's partner/location ordering survives
    // inside each group.
    const orderedRows = useMemo(
        () => [...rows].sort((left, right) => Number(matches(right)) - Number(matches(left))),
        [rows, matches]
    );

    // Derived from the CURRENT rows on every render, so a row that disappeared between fetches can
    // never linger in the payload.
    const selectedRows = useMemo(() => orderedRows.filter(isSelected), [orderedRows, isSelected]);

    const toggleRow = useCallback(
        (row: Parameters<typeof isSelected>[0]) => {
            const next = !isSelected(row);
            setOverrides((previous) => new Map(previous).set(row.workingUnitLocationId, next));
        },
        [isSelected]
    );

    const toggleAll = useCallback(() => {
        // Decided from the CURRENT rows rather than from the override map: a background refetch can leave
        // a stale entry in it for a row that no longer exists, and keying off the map would make the
        // header prune that ghost instead of clearing the visible selection.
        const select = selectedRows.length < orderedRows.length;
        setOverrides(new Map(orderedRows.map((row) => [row.workingUnitLocationId, select])));
    }, [orderedRows, selectedRows]);

    // CALL-775 pattern: the mutation's isLoading cannot disable the rendered button within the same
    // tick, so a double click would otherwise issue two identical bulk requests.
    const submittingRef = useRef(false);

    const handleConfirm = useCallback(async () => {
        if (submittingRef.current || !selectedRows.length) return;

        submittingRef.current = true;
        try {
            await bulkCreateAsync(
                selectedRows.map((row) => ({
                    workingUnitLocationId: row.workingUnitLocationId,
                    budgetBucketId: bucketId,
                }))
            );
            onClose();
        } catch {
            // The mutation's onError already raised the toast and its onSettled refetched the preview,
            // so keep the dialog open: a concurrent duplicate is gone from the list on the retry.
        } finally {
            submittingRef.current = false;
        }
    }, [selectedRows, bulkCreateAsync, bucketId, onClose]);

    const identifierCell = (value: string | null) => <TableCell className="notranslate">{value ?? ''}</TableCell>;

    const renderBody = () => {
        if (isError) return <LoadError error={error} onRetry={refetch} />;

        // Deliberately before the empty state: an in-flight first fetch has no rows yet, and claiming
        // "nothing is missing" then would be a definitive wrong answer rather than a wait.
        if (isFetching) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={28} />
                </Box>
            );
        }

        if (!rows.length) {
            return (
                <Typography variant="body2" color="text.secondary">
                    <span>{t('hint.noMissingConfigs')}</span>
                </Typography>
            );
        }

        return (
            <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <span>{t('hint.completeMissingIntro', { count: rows.length, bucket: bucketName })}</span>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography variant="body2">
                        <span>{t('hint.preselectMatches')}</span>
                    </Typography>
                    {MATCH_FIELDS.map(({ field, labelKey }) => (
                        <FormControlLabel
                            key={field}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={matchOn[field]}
                                    onChange={(event) =>
                                        setMatchOn((previous) => ({ ...previous, [field]: event.target.checked }))
                                    }
                                />
                            }
                            label={t(labelKey)}
                        />
                    ))}
                </Box>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    onChange={toggleAll}
                                    checked={selectedRows.length === orderedRows.length}
                                    indeterminate={selectedRows.length > 0 && selectedRows.length < orderedRows.length}
                                    style={{ padding: 0 }}
                                />
                            </TableCell>
                            <TableCell>{t('field.partner')}</TableCell>
                            <TableCell>{t('field.location')}</TableCell>
                            <TableCell>{t('field.worktype')}</TableCell>
                            <TableCell>{t('field.channel')}</TableCell>
                            <TableCell>{t('field.task')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orderedRows.map((row) => (
                            <TableRow key={row.workingUnitLocationId} hover selected={matches(row)}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={isSelected(row)}
                                        onChange={() => toggleRow(row)}
                                        style={{ padding: 0 }}
                                    />
                                </TableCell>
                                {identifierCell(row.partnerName)}
                                {identifierCell(row.locationName)}
                                {identifierCell(row.worktypeName)}
                                {identifierCell(row.channelName)}
                                {identifierCell(row.taskName)}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </>
        );
    };

    return (
        <ResponsiveDialog
            open={open}
            onClose={onClose}
            title={t('dialog.completeMissingTitle')}
            isConfirmationDialog
            onConfirm={handleConfirm}
            // The count changes on every checkbox click, so it must opt out of translation: a span
            // wrap alone does not save in-place-updating text from the extension DOM rewrite that
            // crashes React's next update of those nodes.
            confirmButtonText={
                <span className="notranslate">{t('action.createSelected', { count: selectedRows.length })}</span>
            }
            // Deselecting everything would otherwise post an empty `configs` array, which the backend
            // schema rejects (min 1).
            disableConfirmButton={selectedRows.length === 0 || isFetching || isError}
            isLoading={isCreating}
            maxWidth="md"
        >
            <Box sx={{ mt: 1, minWidth: 400 }}>{renderBody()}</Box>
        </ResponsiveDialog>
    );
};

export default CompleteMissingConfigsDialog;
